import {
	Card,
	Currency,
	Dropdown,
	Input,
	Label,
	Popup,
} from '@mairistem/composants';
import {
	Combobox,
	type Etiquette,
	Icon,
	UserRole,
} from '@mairistem/finances-utils';
import dayjs from 'dayjs';
import React from 'react';
import StatutMontantSimulation from '../../../../constants/simulation/statutMontantSimulation';
import TypePeriode from '../../../../constants/simulation/typePeriode';
import type {
	default as MontantSimulation,
	MontantSimulationUpdate,
} from '../../../../interfaces/simulation/montantSimulation';
import {
	getMontantColumnFromPeriode,
	getPeriodeFromMontantColumn,
} from '../../../../utils/feuilleSaisie/imputation';
import { isMontantSansOrigine } from '../../../../utils/montantSimulation';
import {
	estIssueDIdee,
	getMotifSuppressionIdee,
	MOTIF_DUPLICATION_IDEE,
} from '../../../../utils/simulation/demandeIdee';
import { COULEURS_ETIQUETTES } from '../../../../utils/simulation/etiquette';
import isInputDisabled from '../../../../utils/simulation/input';
import {
	getCouleurUtilisateur,
	getInitiales,
	getNomUtilisateur,
	LOGIN_NON_RENSEIGNE,
} from '../../../../utils/simulation/utilisateur';
import './MontantsSimulationModalContentItem.css';
import OrigineIdeePopup from './OrigineIdeePopup';

export interface Service {
	'data-testid': string;
	key: number;
	text: string;
	value: number;
}

// Recupere l'element dont le libelle est effectivement tronque a l'affichage
// (le texte simple lui-meme, ou le texte interne de la combobox)
const getElementLibelleService = (container: HTMLElement | null) => {
	if (!container) return null;

	if (
		container.classList.contains(
			'montantsSimulationModalContentItemServiceTexte',
		)
	) {
		return container;
	}

	return container.querySelector<HTMLElement>('.text');
};

interface MontantsSimulationModalContentItemProps {
	// Sans gestion des demandes, une seule ligne sans origine peut etre creee :
	// la duplication est alors impossible des que cette ligne existe
	creationLigneImpossible?: boolean;
	EtiquettesInput: any;
	montantSimulation: MontantSimulation;
	onChange: (data: MontantSimulationUpdate) => void;
	onChangeCommentaire: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChangeMontant: (
		differenceMontant: number,
		typePeriode: TypePeriode,
		nameColumn: string,
	) => void;
	identifiantLoginUtilisateur?: number;
	identifiantsServicesUtilisateur: number[];
	// Libelle de l'imputation, affiche a droite du service (ou du nom de
	// l'utilisateur sans gestion par service) quand la carte est presentee
	// hors du contexte d'une ligne budgetaire
	libelleImputation?: string;
	onDelete: (montantSimulation: MontantSimulation) => void;
	onDuplicate: (montantSimulation: MontantSimulation) => void;
	services: Service[];
	servicesLoading: boolean;
	showRar: boolean;
	showServiceCombobox: boolean;
	transfertCpta: boolean;
	typePeriode: TypePeriode;
	userRole: UserRole;
}

const statutOptions = [
	{
		content: <Label color="olive" content="Accepté" />,
		'data-testid': `statut${StatutMontantSimulation.ACCEPTE}`,
		key: StatutMontantSimulation.ACCEPTE,
		text: 'Accepté',
		value: StatutMontantSimulation.ACCEPTE,
	},
	{
		content: <Label color="teal" content="À arbitrer" />,
		'data-testid': `statut${StatutMontantSimulation.A_ARBITRER}`,
		key: StatutMontantSimulation.A_ARBITRER,
		text: 'À arbitrer',
		value: StatutMontantSimulation.A_ARBITRER,
	},
	{
		content: <Label color="red" content="Refusé" />,
		'data-testid': `statut${StatutMontantSimulation.REFUSE}`,
		key: StatutMontantSimulation.REFUSE,
		text: 'Refusé',
		value: StatutMontantSimulation.REFUSE,
	},
];

const renderStatutLabel = (montantStatut: string) =>
	statutOptions.find((statutOption) => statutOption.value === montantStatut)
		.content;

const MontantsSimulationModalContentItem = ({
	creationLigneImpossible = false,
	EtiquettesInput,
	identifiantLoginUtilisateur,
	identifiantsServicesUtilisateur,
	libelleImputation,
	montantSimulation,
	onChange,
	onChangeCommentaire,
	onChangeMontant,
	onDelete,
	onDuplicate,
	services,
	servicesLoading,
	showRar,
	showServiceCombobox,
	transfertCpta,
	typePeriode,
	userRole,
}: MontantsSimulationModalContentItemProps) => {
	const [login, setLogin] = React.useState(LOGIN_NON_RENSEIGNE);

	React.useEffect(() => {
		let isMounted = true;

		getNomUtilisateur(montantSimulation.identifiantLogin).then((value) => {
			if (isMounted) setLogin(value);
		});

		return () => {
			isMounted = false;
		};
	}, [montantSimulation.identifiantLogin]);

	const handleBlurCommentaire = (e: React.FocusEvent<HTMLInputElement>) => {
		// Optimisation: si meme valeur qu'avant on ne fait pas le onChange
		if (montantSimulation.commentaire === e.currentTarget.value) return;

		// Mise a jour du commentaire sur le montant de simulation
		onChange({ commentaire: e.currentTarget.value });
		// Mise a jour du commentaire dans la treeView
		onChangeCommentaire(e);
	};

	const handleBlurMontant = (
		{ currentTarget: { name } }: React.FocusEvent<HTMLInputElement>,
		montant: number,
	) => {
		// Optimisation: si meme valeur qu'avant on ne fait pas le onChange
		if (montantSimulation[name] === montant) return;

		// Quand on saisit un montant en Propose ou Vote, on le recopie dans
		// Demande tant que celui-ci n'est pas encore renseigne (null ou 0)
		const doitRecopierDemande =
			(name === 'prop' || name === 'vote') && !montantSimulation.demande;

		// Mise a jour du montant sur le montant de simulation
		onChange({
			[name]: montant,
			...(doitRecopierDemande ? { demande: montant } : {}),
		});

		const periodeFromMontant = getPeriodeFromMontantColumn(name);

		// Mise a jour du montant dans la TreeView uniquement si on est sur la
		// periode ; au CA, le statut (herite d'une periode precedente) n'a plus
		// cours et ne doit donc plus exclure la mise a jour du rar
		if (
			(periodeFromMontant === typePeriode &&
				(isCA ||
					montantSimulation.statut !== StatutMontantSimulation.REFUSE)) ||
			name === 'rep'
		) {
			onChangeMontant(
				montant - (montantSimulation[name] ?? 0),
				typePeriode,
				name,
			);
		}
	};

	const handleChangeStatut = (
		_e: React.SyntheticEvent<HTMLElement, Event>,
		{ value }: { value: string },
	) => {
		let differenceMontant = 0;
		const oldStatut = montantSimulation.statut;

		switch (typePeriode) {
			case TypePeriode.DEMANDE:
				if (oldStatut === StatutMontantSimulation.REFUSE) {
					differenceMontant = montantSimulation.demande ?? 0;
				} else if (value === StatutMontantSimulation.REFUSE) {
					differenceMontant = -(montantSimulation.demande ?? 0);
				}
				break;
			case TypePeriode.PROPOSE:
				switch (value) {
					case StatutMontantSimulation.A_ARBITRER:
					case StatutMontantSimulation.REFUSE:
						// En a arbitrer/refuse, on remet la proposition a 0
						differenceMontant = -(montantSimulation.prop ?? 0);
						break;
					case StatutMontantSimulation.ACCEPTE:
						// En accepte, on copie la demande dans la proposition
						differenceMontant =
							(montantSimulation.demande ?? 0) - (montantSimulation.prop ?? 0);
						break;
					default:
						break;
				}
				break;
			case TypePeriode.VOTE:
				// Meme regle que l'API (typePeriode transmis avec le statut)
				switch (value) {
					case StatutMontantSimulation.A_ARBITRER:
					case StatutMontantSimulation.REFUSE:
						// En a arbitrer/refuse, on remet le vote a 0
						differenceMontant = -(montantSimulation.vote ?? 0);
						break;
					case StatutMontantSimulation.ACCEPTE:
						// En accepte, on copie la demande dans le vote s'il est vide
						if (!montantSimulation.vote) {
							differenceMontant = montantSimulation.demande ?? 0;
						}
						break;
					default:
						break;
				}
				break;
			default:
				break;
		}

		// La difference porte toujours sur la colonne de la periode en cours
		onChangeMontant(
			differenceMontant,
			typePeriode,
			getMontantColumnFromPeriode(typePeriode),
		);

		// Un montant refuse ne doit plus rien proposer ni voter, quelle que soit
		// la periode : on remet explicitement prop et vote a 0
		const montantsRefuse =
			value === StatutMontantSimulation.REFUSE ? { prop: 0, vote: 0 } : {};

		// La periode indique a l'API le montant (prop ou vote) a mettre a jour
		onChange({ statut: value, typePeriode, ...montantsRefuse });
	};

	const handleChangeService = (
		_e: React.SyntheticEvent<HTMLElement, Event>,
		{ value }: { value: number },
	) => {
		onChange({ identifiantSvutCpta: value });
	};

	// Depuis un CA, les montants rep/demande/prop/vote n'ont plus de sens : seul
	// le reste a realiser (rar) est affiche
	const isCA = typePeriode === TypePeriode.CA_CFU;

	const statut = montantSimulation.statut;

	// Ligne issue d'une APCP : statut et montants figes, duplication interdite,
	// menu d'actions reserve a l'Admin/ChargePilotage
	const ligneApcp = (montantSimulation.identifiantProImputations ?? 0) !== 0;

	// Seul le champ correspondant a l'etape en cours est actif : Demande tant
	// que le montant est a arbitrer, puis Propose une fois accepte (Vote une
	// fois accepte si on est en periode de vote)
	const demandeActifParStatut = statut === StatutMontantSimulation.A_ARBITRER;
	const propActifParStatut =
		statut === StatutMontantSimulation.ACCEPTE &&
		typePeriode !== TypePeriode.VOTE;
	const voteActifParStatut =
		statut === StatutMontantSimulation.ACCEPTE &&
		typePeriode === TypePeriode.VOTE;

	// Tant qu'aucun service n'a ete choisi (montant en cours de creation), on
	// affiche par defaut le premier service de la liste plutot que "Aucun service"
	const identifiantSvutCptaAffiche =
		montantSimulation.identifiantSvutCpta || (services[0]?.value ?? 0);

	// En dessous de Charge de pilotage (Gestionnaire limite de plus a la periode
	// de demande via isInputDisabled), le service n'est modifiable que tant que
	// le montant est encore a arbitrer. Un Admin/ChargePilotage en gestion par
	// service peut modifier le service quel que soit le statut et la periode
	const serviceDisabledParStatut =
		userRole < UserRole.CHARGE_PILOTAGE &&
		statut !== StatutMontantSimulation.A_ARBITRER;

	const serviceSelectionne = services.find(
		(service) => service.value === identifiantSvutCptaAffiche,
	);

	// Quand la gestion de service est active, un Gestionnaire/Normal ne peut agir
	// que sur les montants dont le service fait partie de ses propres services.
	// On se base sur le service effectivement affiche (avec son repli sur le
	// premier service disponible) plutot que sur la valeur brute du montant,
	// sans quoi un montant sans service enregistre serait bloque a tort alors
	// qu'il affiche deja un service qui appartient bien au Gestionnaire
	const serviceRestriction = showServiceCombobox
		? {
				identifiantSvutCpta: identifiantSvutCptaAffiche,
				identifiantsServicesUtilisateur,
			}
		: undefined;

	// Quand la gestion de service est active, un Gestionnaire/Normal ne peut agir
	// que sur les montants dont il est l'auteur
	const blockNonProprietaire =
		showServiceCombobox &&
		identifiantLoginUtilisateur !== undefined &&
		montantSimulation.identifiantLogin !== identifiantLoginUtilisateur;

	// Un Gestionnaire ne peut agir via le menu burger que sur les demandes a
	// arbitrer dont il est l'auteur ; dans tous les autres cas, le menu doit
	// lui etre masque (et non simplement desactive)
	const montantActionsMenuActifPourGestionnaire =
		typePeriode === TypePeriode.DEMANDE &&
		statut === StatutMontantSimulation.A_ARBITRER &&
		montantSimulation.identifiantLogin === identifiantLoginUtilisateur;

	const montantActionsMenuMasquePourGestionnaire =
		userRole === UserRole.GESTIONNAIRE &&
		!montantActionsMenuActifPourGestionnaire;

	// Une ligne APCP reste habituellement hors de portee du menu d'actions,
	// mais un Admin/ChargePilotage doit tout de meme pouvoir la supprimer
	const montantActionsMenuMasquePourOrigine =
		ligneApcp && userRole < UserRole.CHARGE_PILOTAGE;

	// Demande issue d'une idee Vision 360 : statut et demande figes, duplication
	// interdite, suppression selon la periode et le role
	const issueDIdee = estIssueDIdee(montantSimulation);

	// Motifs affiches en clair dans le menu d'actions, action grisee ; null si
	// l'action est possible. Une demande issue d'une idee ou une ligne APCP ne
	// sont jamais dupliquees, meme par un Admin/ChargePilotage ; sinon, un
	// montant ne se duplique qu'en periode de demande, ou au CA (reste a
	// realiser), et sans gestion des demandes tant qu'aucune ligne sans origine
	// n'existe
	const motifDuplication = issueDIdee
		? MOTIF_DUPLICATION_IDEE
		: ligneApcp
			? 'Duplication impossible pour une ligne APCP'
			: typePeriode !== TypePeriode.DEMANDE &&
					typePeriode !== TypePeriode.CA_CFU
				? 'Duplication impossible dans cette période'
				: creationLigneImpossible
					? 'Une seule ligne sans origine peut être créée'
					: null;
	const motifSuppression = issueDIdee
		? getMotifSuppressionIdee(montantSimulation, typePeriode, userRole)
		: null;

	// Sur une demande issue d'une idee, le menu d'actions reste ouvrable quelle
	// que soit la periode, pour lire les motifs de refus : seuls comptent le
	// transfert en comptabilite, l'auteur et le service du gestionnaire (droits
	// evalues comme en periode de demande)
	const montantActionsMenuMasque = issueDIdee
		? isInputDisabled(
				transfertCpta,
				TypePeriode.DEMANDE,
				userRole,
				true,
				serviceRestriction,
				blockNonProprietaire,
			)
		: montantActionsMenuMasquePourGestionnaire ||
			montantActionsMenuMasquePourOrigine;

	// Tous les champs appliquent la restriction de service : un Gestionnaire ne
	// doit rien pouvoir saisir sur un montant hors de ses propres services (y
	// compris "Aucun service"), meme s'il en est l'auteur
	const champDisabled = (gestionnaireCanWhenDemande = false) =>
		isInputDisabled(
			transfertCpta,
			typePeriode,
			userRole,
			gestionnaireCanWhenDemande,
			serviceRestriction,
			blockNonProprietaire,
		);

	// Hors des cas ou le service est modifiable, ou quand l'utilisateur n'a
	// qu'un seul service au choix, il s'affiche en texte simple, sans selecteur.
	// Comme les autres champs (cf champDisabled), un Gestionnaire ne peut pas
	// rendre editable un montant affichant un service (ou "Aucun service") hors
	// de ses propres services. Comme pour le statut, le service d'une ligne APCP
	// (repris de l'APCP) n'est jamais modifiable, meme par un Admin/ChargePilotage.
	// Le service fleche par une idee Vision 360 n'est jamais modifiable, quel
	// que soit le profil, pour respecter le flechage fait dans Vision 360
	const serviceEditable =
		!champDisabled(true) &&
		!serviceDisabledParStatut &&
		!ligneApcp &&
		!montantSimulation.serviceFlecheParIdee &&
		services.length > 1;

	// Sur une demande issue d'une idee, l'infobulle du service rappelle s'il
	// vient du flechage de l'idee ou s'il a ete affecte faute de service
	const aideServiceIdee = issueDIdee
		? `${montantSimulation.serviceFlecheParIdee ? 'Service fléché' : 'Non fléché'} par l'idée dans Vision 360`
		: undefined;

	// Comme pour le service, le statut s'affiche en texte simple (sans fleche de
	// selecteur) quand il n'est pas modifiable, pour eviter de laisser penser a
	// l'utilisateur qu'il peut interagir avec
	const statutDisabled = champDisabled() || ligneApcp || issueDIdee;

	const serviceNodeRef = React.useRef<HTMLDivElement | null>(null);
	const serviceResizeObserverRef = React.useRef<ResizeObserver | null>(null);
	const [serviceLibelleTronque, setServiceLibelleTronque] =
		React.useState(false);

	// Le tooltip du libelle de service ne doit s'afficher que si le texte est
	// reellement tronque a l'affichage (largeur reelle), pas selon un seuil fixe
	const mesurerTroncatureService = React.useCallback(() => {
		const element = getElementLibelleService(serviceNodeRef.current);
		setServiceLibelleTronque(
			Boolean(element) && element.scrollWidth > element.clientWidth,
		);
	}, []);

	// Callback ref plutot que useRef+useEffect : le Popup ne monte le trigger
	// qu'une fois le libelle detecte comme tronque (cf plus bas), ce qui
	// remplace le noeud DOM - un simple ref ne serait alors plus re-observe
	const serviceTriggerRef = React.useCallback(
		(node: HTMLDivElement | null) => {
			serviceNodeRef.current = node;
			serviceResizeObserverRef.current?.disconnect();
			serviceResizeObserverRef.current = null;

			const element = getElementLibelleService(node);
			if (!element) {
				setServiceLibelleTronque(false);
				return;
			}

			if (typeof ResizeObserver !== 'undefined') {
				serviceResizeObserverRef.current = new ResizeObserver(
					mesurerTroncatureService,
				);
				serviceResizeObserverRef.current.observe(element);
			}

			mesurerTroncatureService();
		},
		[mesurerTroncatureService],
	);

	// Re-mesure quand le libelle affiche change (meme noeud DOM, contenu
	// different), et une fois les polices chargees (largeur du texte stable)
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-mesure quand le libelle change, meme si l'effet ne le lit qu'au travers du DOM
	React.useLayoutEffect(() => {
		mesurerTroncatureService();

		document.fonts?.ready?.then(mesurerTroncatureService);
	}, [mesurerTroncatureService, serviceSelectionne?.text]);

	// Le libelle de l'imputation suit le service s'il est affiche, sinon le nom
	const avecImputation = (contenu: React.ReactNode) =>
		libelleImputation ? (
			<div className="montantsSimulationModalContentItemLoginLigne">
				{contenu}
				<span
					className="montantsSimulationModalContentItemImputation"
					data-testid="libelleImputation"
				>
					{libelleImputation}
				</span>
			</div>
		) : (
			contenu
		);

	const loginTexte = <strong>{login}</strong>;

	return (
		<Card className="montantsSimulationModalContentItem" fluid>
			<Card.Content>
				<div
					className={[
						'montantsSimulationModalContentItemGrid',
						isCA && 'montantsSimulationModalContentItemGridCA',
						!isCA && !showRar && 'montantsSimulationModalContentItemGridNoRar',
					]
						.filter(Boolean)
						.join(' ')}
				>
					<div className="montantsSimulationModalContentItemLogin">
						{login !== LOGIN_NON_RENSEIGNE && (
							<Label
								circular
								className="montantsSimulationModalContentItemLoginAvatar"
								color={getCouleurUtilisateur(
									montantSimulation.identifiantLogin,
								)}
							>
								{getInitiales(login)}
							</Label>
						)}
						<div className="montantsSimulationModalContentItemLoginInfos">
							{showServiceCombobox ? loginTexte : avecImputation(loginTexte)}
							{showServiceCombobox &&
								(() => {
									const serviceTrigger = serviceEditable ? (
										<div ref={serviceTriggerRef}>
											<Combobox
												data-testid="serviceCombobox"
												disabled={false}
												loading={servicesLoading}
												onChange={handleChangeService}
												options={services}
												value={identifiantSvutCptaAffiche}
											/>
										</div>
									) : (
										<div
											className="montantsSimulationModalContentItemServiceTexte"
											data-testid="serviceTexte"
											ref={serviceTriggerRef}
										>
											{serviceSelectionne?.text}
										</div>
									);

									// Le Popup n'est monte que si le libelle est reellement
									// tronque (ou pour l'aide d'une demande issue d'une idee) :
									// le monter des le depart puis basculer sa prop `disabled`
									// ne re-attache pas correctement le survol
									const contenuPopup = aideServiceIdee ? (
										<span data-testid="serviceAideIdee">
											{serviceLibelleTronque && (
												<>
													{serviceSelectionne?.text}
													<br />
												</>
											)}
											{aideServiceIdee}
										</span>
									) : (
										serviceLibelleTronque && serviceSelectionne?.text
									);
									return avecImputation(
										contenuPopup ? (
											<Popup
												content={contenuPopup}
												position="top center"
												wide
												trigger={serviceTrigger}
											/>
										) : (
											serviceTrigger
										),
									);
								})()}
						</div>
					</div>
					<div className="montantsSimulationModalContentItemStatut">
						<div className="montantsSimulationModalContentItemDateCreation">
							{montantSimulation.dateCreation
								? dayjs(montantSimulation.dateCreation).format('DD/MM/YYYY')
								: ''}
						</div>
						<div className="montantsSimulationModalContentItemStatutActions">
							{!isCA &&
								(statutDisabled ? (
									<div
										className="montantsSimulationModalContentItemStatutTexte montantsSimulationModalContentItemStatutValue"
										data-testid="statutTexte"
									>
										{renderStatutLabel(statut)}
									</div>
								) : (
									<Combobox
										className="montantsSimulationModalContentItemStatutValue"
										data-testid="statutCombobox"
										disabled={false}
										onChange={handleChangeStatut}
										options={statutOptions}
										// @ts-expect-error we can render JSX and not only string
										text={() => renderStatutLabel(statut)}
										value={statut}
									/>
								))}
							<Dropdown
								className={`montantsSimulationModalContentItemActionsMenu${
									montantActionsMenuMasque
										? ' montantsSimulationModalContentItemActionsMenuHidden'
										: ''
								}`}
								data-testid="montantActionsMenu"
								direction="left"
								disabled={
									montantActionsMenuMasque ||
									(!issueDIdee && champDisabled(true))
								}
								// Meme presentation que les actions de la TreeView : l'icone
								// seule, sans bouton ni fleche de liste
								icon={null}
								trigger={<Icon cursor="pointer" iconSet="Lucide" name="Menu" />}
							>
								<Dropdown.Menu>
									<Dropdown.Item
										data-testid="duplicateMontant"
										disabled={!!motifDuplication}
										onClick={() => onDuplicate(montantSimulation)}
										text={motifDuplication ?? 'Dupliquer'}
									/>
									<Dropdown.Item
										data-testid="deleteMontant"
										disabled={!!motifSuppression}
										onClick={() => onDelete(montantSimulation)}
										text={motifSuppression ?? 'Supprimer'}
									/>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</div>
					{(isCA || showRar) && (
						<div className="montantsSimulationModalContentItemField montantsSimulationModalContentItemRar">
							<div className="montantsSimulationModalContentItemFieldLabel">
								{isCA ? 'Reste à réaliser' : 'RAR'}
							</div>
							<Currency
								data-testid={isCA ? 'rarInput' : 'repInput'}
								disabled={champDisabled() || ligneApcp}
								fluid
								name={isCA ? 'rar' : 'rep'}
								onBlur={handleBlurMontant}
								value={isCA ? montantSimulation.rar : montantSimulation.rep}
							/>
						</div>
					)}
					{!isCA && (
						<div className="montantsSimulationModalContentItemField montantsSimulationModalContentItemDemande">
							<div className="montantsSimulationModalContentItemFieldLabel">
								Demandé
							</div>
							<Currency
								data-testid="demandeInput"
								disabled={
									champDisabled(true) ||
									!demandeActifParStatut ||
									ligneApcp ||
									// Le demande d'une demande issue d'une idee reste egal au
									// montant de l'idee
									issueDIdee
								}
								fluid
								name="demande"
								onBlur={handleBlurMontant}
								value={montantSimulation.demande}
							/>
						</div>
					)}
					{!isCA && (
						<div className="montantsSimulationModalContentItemField montantsSimulationModalContentItemProp">
							<div className="montantsSimulationModalContentItemFieldLabel">
								Proposé
							</div>
							<Currency
								data-testid="propInput"
								disabled={champDisabled() || !propActifParStatut || ligneApcp}
								fluid
								name="prop"
								onBlur={handleBlurMontant}
								value={montantSimulation.prop}
							/>
						</div>
					)}
					{!isCA && (
						<div className="montantsSimulationModalContentItemField montantsSimulationModalContentItemVote">
							<div className="montantsSimulationModalContentItemFieldLabel">
								Voté
							</div>
							<Currency
								data-testid="voteInput"
								disabled={champDisabled() || !voteActifParStatut || ligneApcp}
								fluid
								name="vote"
								onBlur={handleBlurMontant}
								value={montantSimulation.vote}
							/>
						</div>
					)}
					<div className="montantsSimulationModalContentItemField montantsSimulationModalContentItemCommentaire">
						<div className="montantsSimulationModalContentItemFieldLabel">
							Commentaire
						</div>
						<Input
							disabled={champDisabled(true)}
							fluid
							onBlur={handleBlurCommentaire}
							value={montantSimulation.commentaire}
						>
							<input data-testid="commentaireInput" maxLength={255} />
						</Input>
					</div>
					{!isMontantSansOrigine(montantSimulation) && (
						<div className="montantsSimulationModalContentItemField montantsSimulationModalContentItemOrigine">
							<div className="montantsSimulationModalContentItemFieldLabel">
								Origine
							</div>
							<div className="montantsSimulationModalContentItemOrigineValue">
								{montantSimulation.type}
								{/* idee n'est renseignee que pour une demande issue d'une idee */}
								{montantSimulation.idee && (
									<OrigineIdeePopup
										demande={montantSimulation.demande}
										idee={montantSimulation.idee}
									/>
								)}
							</div>
						</div>
					)}
					<div className="montantsSimulationModalContentItemEtiquettes">
						<EtiquettesInput
							colors={COULEURS_ETIQUETTES}
							dropdownProps={{
								disabled: champDisabled(true),
							}}
							onChange={(data: {
								etiquettes: Etiquette[] | Etiquette['identifiant'];
							}) => {
								const etiquettes = Array.isArray(data.etiquettes)
									? data.etiquettes
									: [data.etiquettes];

								onChange({
									etiquettes: etiquettes.map((etiquette) => ({
										identifiant:
											typeof etiquette === 'number'
												? etiquette
												: etiquette.identifiant,
									})) as Etiquette[],
								});
							}}
							selectedEtiquettes={montantSimulation.etiquettes}
						/>
					</div>
				</div>
			</Card.Content>
		</Card>
	);
};

export default MontantsSimulationModalContentItem;
